import React from 'react';
import { LogAnalysisResult, DataDictionaryEntry, ValueCount, FieldType } from '../../models/LogAnalysis';
import SortableTable, { ColumnDefinition } from '../common/SortableTable';
import './CsvAnalysis.css';

interface CsvAnalysisResultProps {
  analysisData: LogAnalysisResult | null;
  error: string | null;
  isLoading: boolean;
}

const CsvAnalysisResultDisplay: React.FC<CsvAnalysisResultProps> = ({ analysisData, error, isLoading }) => {
  if (isLoading) {
    return <div className="loading-message">Loading analysis results...</div>;
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  if (!analysisData) {
    return <div className="info-message">Submit CSV data to see the analysis.</div>;
  }

  const { summary, dataDictionary,  } = analysisData;


  const dataDictionaryColumns: ColumnDefinition<DataDictionaryEntry>[] = [
    { key: 'fieldName', label: 'Field Name', sortable: true },
    { key: 'fieldType', label: 'Field Type', sortable: true },
    { key: 'cardinality', label: 'Cardinality', sortable: true, align: 'right' },
  ];

  // Define columns for the field type distribution table
  const fieldTypeDistributionColumns: ColumnDefinition<{ type: string; count: number }>[] = [
    { key: 'type', label: 'Field Type', sortable: true, className: 'fieldType-label' },
    { key: 'count', label: 'Count', sortable: true, align: 'right' },
  ];

  const fieldPivots: ColumnDefinition<ValueCount>[] = [
    { key: 'value', label: 'Field Value', sortable: true },
    { key: 'count', label: 'Count', sortable: true, align: 'right' },
  ];

  const showFieldHeader = ( dde: DataDictionaryEntry) => {

    const ddeFirstValue = dde.pivots? dde.pivots[0].value : "";

    return (
      <div>
        <h4>{dde.fieldName}</h4>
        {dde.fieldType === FieldType.NoData && <p>This field has NO DATA values.</p>}
        {dde.fieldType === FieldType.OneValue && <p>This field has just ONE value: [{ddeFirstValue}]</p>}
        {dde.fieldType === FieldType.SelectValues && <p>This field has {dde.cardinality} unique values.</p>}
        {dde.fieldType === FieldType.ManyValues && <p>This field has just TOO MANY ({dde.cardinality}) unique values (details not shown).</p>}        
        {dde.fieldType === FieldType.AllUnique && <p>This field has ALL UNIQUE values (ex: an ID field).</p>}
        {dde.fieldType === FieldType.TimeStamp && <p>This field is a timestamp. Min = [{dde.minTimestamp}], Max = [{dde.maxTimestamp}]</p>}
      </div>
    )
  }

  return (
    <div className="csv-analysis-result-container">
      <h3>Analysis Summary</h3>
      <div className="summary-section">
        <p><strong>Data Title:</strong> {summary.dataTitle}</p>
        <p><strong>Number of Fields:</strong> {summary.numFields}</p>
        <p><strong>Number of Rows:</strong> {summary.numRows}</p>
        {summary.fieldTypeDistribution && (
          <div className="field-type-distribution">
            <h4>Field Type Distribution</h4>
            <SortableTable
              columns={fieldTypeDistributionColumns}
              data={Object.entries(summary.fieldTypeDistribution).map(([type, count]) => ({
                type,
                count,
              }))}
              defaultSortConfig={{ key: 'type', direction: 'ascending' }}
              // Assuming 'type' is unique for rowIdKey, or generate a unique ID if necessary
              rowIdKey="type"
            />
          </div>
        )}
      </div>

      <hr/>
      <h3>Data Dictionary</h3>
      {dataDictionary && dataDictionary.length > 0 ? (
        <SortableTable
          columns={dataDictionaryColumns}
          data={dataDictionary}
          defaultSortConfig={{ key: 'fieldName', direction: 'ascending' }}
          rowIdKey="fieldName"
        />
      ) : (
        <p>No data dictionary information available.</p>
      )}

      <hr/>
      <h3>Per-Field Pivots</h3>
      {dataDictionary && dataDictionary.length > 0 && (
        <>
          {Object.entries( dataDictionary).map( ([indexKey, dde]) => (
            <div className="per-field-summary" key={indexKey}>
            {showFieldHeader(dde)}
            {dde.pivots && dde.pivots.length > 0 && dde.fieldType === FieldType.SelectValues && (
              <SortableTable
                    columns={fieldPivots}
                    data={dde.pivots}
                    defaultSortConfig={{ key: 'count', direction: 'descending' }}
                    rowIdKey="value"
                />
            )}
            </div>
          ))}
        </>
      )}

      {analysisData.error && (
        <div className="error-message">
            <h4>Analysis Error:</h4>
            <p>{analysisData.error}</p>
        </div>
      )}
    </div>
  );
};

export default CsvAnalysisResultDisplay;